import React, { useEffect, useRef, useState } from "react";
import {
    Box,
    Typography,
    Chip,
    CircularProgress,
    Alert,
    Divider,
    IconButton,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Stack,
    Avatar,
    Dialog,
    DialogTitle,
    DialogContent,
    Tooltip,
} from "@mui/material";
import {
    ArrowBackIos as BackIcon,
    ExpandMore as ExpandMoreIcon,
    LocationOn as LocationIcon,
    Group as ContactsIcon,
    AttachFile as FilesIcon,
    Info as InfoIcon,
    Notes as NotesIcon,
    CheckCircle as CompleteIcon,
    ReportProblem as UnreconciledIcon,
    Phone as PhoneIcon,
    Email as EmailIcon,
    OpenInNew as MapsIcon,
    PictureAsPdf as PdfIcon,
    Image as ImageIcon,
    Close as CloseIcon,
} from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import ProjectService from "../services/Project.service";
import { usePermissions } from "../hooks/usePermissions";
import { getGradeChipSx, getGradeLabel } from "../utils/gradeColors";

// ── helpers ────────────────────────────────────────────────────────────────────

function LabelValue({ label, value }) {
    if (!value && value !== 0) return null;
    return (
        <Box sx={{ mb: 1.5 }}>
            <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={600}
                sx={{ textTransform: "uppercase", letterSpacing: 0.4 }}
            >
                {label}
            </Typography>
            <Typography variant="body2">{value}</Typography>
        </Box>
    );
}

function FlagChip({ label }) {
    return <Chip label={label} size="small" color="primary" />;
}

function fileIcon(title = "") {
    return title.toLowerCase().endsWith(".pdf") ? (
        <PdfIcon fontSize="small" color="error" />
    ) : (
        <ImageIcon fontSize="small" color="primary" />
    );
}

// ── PDF viewer dialog ──────────────────────────────────────────────────────────

function PdfViewerDialog({ file, onClose }) {
    const isOpen = Boolean(file);
    const isPdf = file?.title?.toLowerCase().endsWith(".pdf");
    const [blobUrl, setBlobUrl] = useState(null);
    const [loadError, setLoadError] = useState(false);
    const prevUrlRef = useRef(null);

    useEffect(() => {
        if (!file?.document_url || !isPdf) {
            setBlobUrl(null);
            setLoadError(false);
            return;
        }
        if (prevUrlRef.current === file.document_url && blobUrl) return;
        prevUrlRef.current = file.document_url;
        setBlobUrl(null);
        setLoadError(false);

        fetch(file.document_url, { credentials: "include" })
            .then((res) => {
                if (!res.ok) throw new Error("fetch failed");
                return res.blob();
            })
            .then((blob) => {
                const url = URL.createObjectURL(blob);
                setBlobUrl(url);
            })
            .catch(() => setLoadError(true));

        return () => {
            if (blobUrl) URL.revokeObjectURL(blobUrl);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [file?.document_url, isPdf]);

    const handleClose = () => {
        if (blobUrl) {
            URL.revokeObjectURL(blobUrl);
            setBlobUrl(null);
        }
        prevUrlRef.current = null;
        onClose();
    };

    return (
        <Dialog open={isOpen} onClose={handleClose} fullScreen>
            <DialogTitle
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    py: 1,
                    px: 2,
                    borderBottom: 1,
                    borderColor: "divider",
                }}
            >
                <Typography
                    component="span"
                    variant="subtitle2"
                    noWrap
                    sx={{ flex: 1, mr: 1 }}
                >
                    {file?.title}
                </Typography>
                <Tooltip title="Open in new tab">
                    <IconButton
                        size="small"
                        component="a"
                        href={file?.document_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ mr: 0.5 }}
                    >
                        <MapsIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
                <IconButton size="small" onClick={handleClose}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{ p: 0, display: "flex", flexDirection: "column" }}>
                {isPdf ? (
                    loadError ? (
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", flex: 1 }}>
                            <Typography color="error">
                                Unable to load PDF.{" "}
                                <a href={file?.document_url} target="_blank" rel="noopener noreferrer">
                                    Open in new tab
                                </a>
                            </Typography>
                        </Box>
                    ) : blobUrl ? (
                        <Box
                            component="iframe"
                            src={blobUrl}
                            title={file?.title}
                            sx={{ flex: 1, width: "100%", border: "none", minHeight: 0 }}
                        />
                    ) : (
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", flex: 1 }}>
                            <CircularProgress />
                        </Box>
                    )
                ) : (
                    <Box
                        sx={{
                            flex: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            p: 2,
                            bgcolor: "grey.100",
                        }}
                    >
                        <Box
                            component="img"
                            src={file?.document_url}
                            alt={file?.title}
                            sx={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
                        />
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
}

// ── main component ─────────────────────────────────────────────────────────────

function ProjectDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { isSubcontractor } = usePermissions();
    const [project, setProject] = useState(null);
    const [contacts, setContacts] = useState([]);
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [expanded, setExpanded] = useState("info");
    const [viewerFile, setViewerFile] = useState(null);

    useEffect(() => {
        setLoading(true);
        setError("");
        Promise.all([
            ProjectService.get(id),
            ProjectService.getContacts(id),
        ])
            .then(([projRes, contactRes]) => {
                const proj = projRes.data;
                setProject(proj);

                const contactData = contactRes.data?.results ?? contactRes.data ?? [];
                setContacts(contactData.filter((c) => !c.is_archived));

                // Project-level attachments only
                const projectAttachments = (proj.attachments_detail || [])
                    .filter((a) => a.document_url);
                setFiles(projectAttachments);
            })
            .catch(() => setError("Failed to load project details."))
            .finally(() => setLoading(false));
    }, [id]);

    const toggle = (panel) => (_, isOpen) => setExpanded(isOpen ? panel : false);

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error || !project) {
        return (
            <Box>
                <IconButton size="small" onClick={() => navigate(-1)} sx={{ mb: 1 }}>
                    <BackIcon fontSize="small" />
                </IconButton>
                <Alert severity="error">{error || "Project not found."}</Alert>
            </Box>
        );
    }

    const showGrade = !isSubcontractor && project.profit_grade;
    const profitScore =
        project.profit_score == null || project.profit_score === ""
            ? null
            : Number(project.profit_score);
    const hasProfitScore = profitScore != null && !Number.isNaN(profitScore);
    const gradeLabel = getGradeLabel(project.profit_grade);
    const gradeTooltip = hasProfitScore
        ? `Grade: ${project.profit_grade} (${gradeLabel}) • Score: ${profitScore}${project.profit_margin_pct != null ? ` • Margin: ${parseFloat(project.profit_margin_pct) || 0}%` : ""}`
        : "Grade unavailable";

    const addr = project.address_detail;
    const mapsUrl = addr
        ? `https://maps.google.com/?q=${encodeURIComponent(
              [addr.address, addr.city, addr.state, addr.postal_code]
                  .filter(Boolean)
                  .join(", ")
          )}`
        : null;

    return (
        <Box>
            {/* ── Header ── */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <IconButton size="small" onClick={() => navigate(-1)}>
                    <BackIcon fontSize="small" />
                </IconButton>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                        <Typography
                            variant="h6"
                            fontWeight={700}
                            sx={{ fontFamily: "Montserrat, sans-serif" }}
                        >
                            {project.number}
                        </Typography>
                        {showGrade && (
                            <Tooltip title={gradeTooltip} placement="top">
                                <Chip
                                    label={project.profit_grade}
                                    size="small"
                                    variant="filled"
                                    sx={{
                                        fontWeight: 700,
                                        width: 26,
                                        height: 26,
                                        borderRadius: "50%",
                                        justifyContent: "center",
                                        ...getGradeChipSx(project.profit_grade),
                                        "& .MuiChip-label": { px: 0 },
                                    }}
                                />
                            </Tooltip>
                        )}
                        {project.is_complete && (
                            <Chip
                                icon={<CompleteIcon />}
                                label="Complete"
                                size="small"
                                color="success"
                            />
                        )}
                        {project.has_unreconciled_bom_items && (
                            <Tooltip title="Some shipped items don't match this project's BOM — a packed item has no BOM line, or a line shipped more than its required quantity.">
                                <Chip
                                    icon={<UnreconciledIcon />}
                                    label="BOM Mismatch"
                                    size="small"
                                    color="warning"
                                />
                            </Tooltip>
                        )}
                    </Box>
                    <Typography variant="body2" color="text.secondary" noWrap>
                        {project.name}
                    </Typography>
                </Box>
            </Box>

            {/* ── Project Info ── */}
            <Accordion
                expanded={expanded === "info"}
                onChange={toggle("info")}
                disableGutters
                elevation={0}
                sx={{ border: 1, borderColor: "divider", mb: 1, "&:before": { display: "none" } }}
            >
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: "action.hover" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <InfoIcon fontSize="small" color="primary" />
                        <Typography variant="subtitle2" fontWeight={600}>
                            Project Information
                        </Typography>
                    </Box>
                </AccordionSummary>
                <AccordionDetails>
                    <LabelValue label="Customer" value={project.customer_detail?.name} />
                    <LabelValue label="Category" value={project.project_category_detail?.name} />
                    <LabelValue label="Type" value={project.project_type_detail?.name} />
                    <LabelValue label="Billing" value={project.billing_type_detail?.name} />
                    {project.expected_completion_date && (
                        <LabelValue
                            label="Expected Completion"
                            value={new Date(project.expected_completion_date).toLocaleDateString()}
                        />
                    )}
                    {(project.travel_job ||
                        project.prevailing_rate ||
                        project.union ||
                        project.certified_payroll ||
                        project.tax_exempt) && (
                        <Box sx={{ mt: 1.5 }}>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                fontWeight={600}
                                sx={{
                                    textTransform: "uppercase",
                                    letterSpacing: 0.4,
                                    display: "block",
                                    mb: 0.75,
                                }}
                            >
                                Flags
                            </Typography>
                            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                                {project.travel_job && <FlagChip label="Travel Job" />}
                                {project.prevailing_rate && <FlagChip label="Prevailing Rate" />}
                                {project.union && <FlagChip label="Union" />}
                                {project.certified_payroll && <FlagChip label="Certified Payroll" />}
                                {project.tax_exempt && <FlagChip label="Tax Exempt" />}
                            </Stack>
                        </Box>
                    )}
                </AccordionDetails>
            </Accordion>

            {/* ── Address ── */}
            {addr && (
                <Accordion
                    expanded={expanded === "address"}
                    onChange={toggle("address")}
                    disableGutters
                    elevation={0}
                    sx={{ border: 1, borderColor: "divider", mb: 1, "&:before": { display: "none" } }}
                >
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={{ bgcolor: "action.hover" }}
                    >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <LocationIcon fontSize="small" color="primary" />
                            <Typography variant="subtitle2" fontWeight={600}>
                                Address
                            </Typography>
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5 }}>
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="body2">{addr.address}</Typography>
                                {addr.address_line2 && (
                                    <Typography variant="body2">{addr.address_line2}</Typography>
                                )}
                                <Typography variant="body2">
                                    {[addr.city, addr.state, addr.postal_code]
                                        .filter(Boolean)
                                        .join(", ")}
                                </Typography>
                                {addr.country && addr.country !== "US" && (
                                    <Typography variant="body2">{addr.country}</Typography>
                                )}
                            </Box>
                            {mapsUrl && (
                                <Tooltip title="Open in Maps">
                                    <IconButton
                                        size="small"
                                        component="a"
                                        href={mapsUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        color="primary"
                                    >
                                        <MapsIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Box>
                    </AccordionDetails>
                </Accordion>
            )}

            {/* ── Contacts ── */}
            <Accordion
                expanded={expanded === "contacts"}
                onChange={toggle("contacts")}
                disableGutters
                elevation={0}
                sx={{ border: 1, borderColor: "divider", mb: 1, "&:before": { display: "none" } }}
            >
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: "action.hover" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <ContactsIcon fontSize="small" color="primary" />
                        <Typography variant="subtitle2" fontWeight={600}>
                            Contacts
                        </Typography>
                        {contacts.length > 0 && (
                            <Chip
                                label={contacts.length}
                                size="small"
                                sx={{ height: 18, fontSize: 11 }}
                            />
                        )}
                    </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                    {contacts.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                            No contacts linked to this project.
                        </Typography>
                    ) : (
                        <List disablePadding>
                            {contacts.map((c, idx) => {
                                const phones = Array.isArray(c.phone) ? c.phone : [];
                                const initials = c.name
                                    ? c.name
                                          .split(" ")
                                          .map((w) => w[0])
                                          .slice(0, 2)
                                          .join("")
                                          .toUpperCase()
                                    : "?";
                                return (
                                    <React.Fragment key={c.id}>
                                        {idx > 0 && <Divider component="li" />}
                                        <ListItem
                                            alignItems="flex-start"
                                            sx={{ py: 1.5, px: 2 }}
                                        >
                                            <ListItemIcon sx={{ minWidth: 44, mt: 0.5 }}>
                                                <Avatar
                                                    sx={{
                                                        width: 32,
                                                        height: 32,
                                                        fontSize: 12,
                                                        bgcolor: "primary.main",
                                                    }}
                                                >
                                                    {initials}
                                                </Avatar>
                                            </ListItemIcon>
                                            <ListItemText
                                                disableTypography
                                                primary={
                                                    <Typography
                                                        variant="body2"
                                                        fontWeight={600}
                                                    >
                                                        {c.name}
                                                    </Typography>
                                                }
                                                secondary={
                                                    <Box>
                                                        {c.job_title && (
                                                            <Typography
                                                                variant="caption"
                                                                color="text.secondary"
                                                                display="block"
                                                            >
                                                                {c.job_title}
                                                            </Typography>
                                                        )}
                                                        {c.email && (
                                                            <Box
                                                                sx={{
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    gap: 0.5,
                                                                    mt: 0.5,
                                                                }}
                                                            >
                                                                <EmailIcon
                                                                    sx={{
                                                                        fontSize: 13,
                                                                        color: "text.secondary",
                                                                    }}
                                                                />
                                                                <Typography
                                                                    component="a"
                                                                    href={`mailto:${c.email}`}
                                                                    variant="caption"
                                                                    color="primary"
                                                                    sx={{ textDecoration: "none" }}
                                                                >
                                                                    {c.email}
                                                                </Typography>
                                                            </Box>
                                                        )}
                                                        {phones.map((ph) => (
                                                            <Box
                                                                key={ph.id}
                                                                sx={{
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    gap: 0.5,
                                                                    mt: 0.25,
                                                                }}
                                                            >
                                                                <PhoneIcon
                                                                    sx={{
                                                                        fontSize: 13,
                                                                        color: "text.secondary",
                                                                    }}
                                                                />
                                                                <Typography
                                                                    component="a"
                                                                    href={`tel:${ph.phone_number}`}
                                                                    variant="caption"
                                                                    color="primary"
                                                                    sx={{ textDecoration: "none" }}
                                                                >
                                                                    {ph.phone_number}
                                                                </Typography>
                                                                {ph.phone_type && (
                                                                    <Typography
                                                                        variant="caption"
                                                                        color="text.secondary"
                                                                    >
                                                                        · {ph.phone_type}
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                        ))}
                                                    </Box>
                                                }
                                            />
                                        </ListItem>
                                    </React.Fragment>
                                );
                            })}
                        </List>
                    )}
                </AccordionDetails>
            </Accordion>

            {/* ── Notes ── */}
            <Accordion
                expanded={expanded === "notes"}
                onChange={toggle("notes")}
                disableGutters
                elevation={0}
                sx={{ border: 1, borderColor: "divider", mb: 1, "&:before": { display: "none" } }}
            >
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{ bgcolor: "action.hover" }}
                >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <NotesIcon fontSize="small" color="primary" />
                        <Typography variant="subtitle2" fontWeight={600}>
                            Notes
                        </Typography>
                    </Box>
                </AccordionSummary>
                <AccordionDetails>
                    {project.notes ? (
                        <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                            {project.notes}
                        </Typography>
                    ) : (
                        <Typography variant="body2" color="text.secondary">
                            No notes for this project.
                        </Typography>
                    )}
                </AccordionDetails>
            </Accordion>

            {/* ── Files (Change Order Attachments) ── */}
            <Accordion
                expanded={expanded === "files"}
                onChange={toggle("files")}
                disableGutters
                elevation={0}
                sx={{ border: 1, borderColor: "divider", mb: 1, "&:before": { display: "none" } }}
            >
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: "action.hover" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <FilesIcon fontSize="small" color="primary" />
                        <Typography variant="subtitle2" fontWeight={600}>
                            Files
                        </Typography>
                        {files.length > 0 && (
                            <Chip
                                label={files.length}
                                size="small"
                                sx={{ height: 18, fontSize: 11 }}
                            />
                        )}
                    </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                    {files.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                            No files attached to this project.
                        </Typography>
                    ) : (
                        <List disablePadding>
                            {files.map((f, idx) => (
                                <React.Fragment key={f.id}>
                                    {idx > 0 && <Divider component="li" />}
                                    <ListItemButton
                                        onClick={() => setViewerFile(f)}
                                        sx={{ py: 1, px: 2 }}
                                    >
                                        <ListItemIcon sx={{ minWidth: 36 }}>
                                            {fileIcon(f.title)}
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={f.title}
                                            secondary={f.source}
                                            primaryTypographyProps={{ variant: "body2" }}
                                            secondaryTypographyProps={{ variant: "caption" }}
                                        />
                                    </ListItemButton>
                                </React.Fragment>
                            ))}
                        </List>
                    )}
                </AccordionDetails>
            </Accordion>

            {/* ── PDF / Image Viewer ── */}
            <PdfViewerDialog file={viewerFile} onClose={() => setViewerFile(null)} />
        </Box>
    );
}

export default ProjectDetail;
